import { apiGet, apiPost, apiPut, request } from '@/shared/utils/requestBuilder';
import type { AgentPage } from '../../types/negotiationAgents';
import type {
  AgentSettingMutation,
  AgentSettingMutationResult,
  AgentSettingQuery,
  AgentSettingResource,
  AgentSettingDocument,
  AgentSettingDocumentRequest,
} from '../../types/agentSettings';
import { readAgentResponse } from '../../utils/negotiationAgents/apiResponse';

const base = '/admin/negotiationAgents';
export const agentSettingsService = {
  async page(query: AgentSettingQuery): Promise<AgentPage<AgentSettingResource>> {
    const params = new URLSearchParams({ page: String(query.page), limit: String(query.limit) });
    for (const key of ['search', 'categoryId', 'flowId', 'nodeId', 'subAgentId'] as const)
      if (query[key]) params.set(key, query[key]);
    if (query.includeRemoved) params.set('includeRemoved', 'true');
    if (query.ids?.length) params.set('ids', query.ids.join(','));
    const agentUrl = base + '/' + encodeURIComponent(query.agentId);
    const path =
      query.resource === 'categories'
        ? '/settingCategories'
        : query.resource === 'settings'
          ? '/settings'
          : query.resource === 'targets'
            ? '/settings/targets'
            : query.resource === 'changes'
              ? '/settings/' +
                encodeURIComponent(query.settingId ?? '') +
                '/history/' +
                encodeURIComponent(query.revisionId ?? '') +
                '/bindings'
              : '/settings/' + encodeURIComponent(query.settingId ?? '') + '/' + query.resource;
    return readAgentResponse(await apiGet(agentUrl + path + '?' + params));
  },
  async mutate(input: AgentSettingMutation): Promise<AgentSettingMutationResult> {
    const agentUrl = base + '/' + encodeURIComponent(input.agentId);
    if (input.operation === 'createCategory')
      return readAgentResponse(await apiPost(agentUrl + '/settingCategories', input.data));
    const url =
      agentUrl +
      '/settings' +
      ('settingId' in input && input.settingId ? '/' + encodeURIComponent(input.settingId) : '');
    if (input.operation === 'remove')
      return readAgentResponse(
        await request({ endpoint: url, method: 'DELETE', body: input.data }),
      );
    return readAgentResponse(
      await (input.settingId ? apiPut(url, input.data) : apiPost(url, input.data)),
    );
  },
  async document(input: AgentSettingDocumentRequest): Promise<AgentSettingDocument> {
    const url = base + '/' + encodeURIComponent(input.agentId) + '/settings/documents';
    if (input.file) {
      const body = new FormData();
      body.append('file', input.file);
      return readAgentResponse(await apiPost(url, body));
    }
    if (!input.documentId) throw new Error('Selecione um documento para verificar.');
    return readAgentResponse(await apiGet(url + '/' + encodeURIComponent(input.documentId)));
  },
};
